import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '../services/api';

const criarIconeCaminhao = (placa) => {
  return L.divIcon({
    className: 'custom-truck-marker',
    html: `
      <div style="
        background-color: #007AFF;
        color: white;
        padding: 4px 8px;
        border-radius: 6px;
        font-weight: 800;
        font-size: 11px;
        font-family: sans-serif;
        border: 2px solid #ffffff;
        box-shadow: 0 4px 10px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        text-align: center;
      ">
        <span>🚚</span> <span>${placa}</span>
      </div>
    `,
    iconSize: [80, 30],
    iconAnchor: [40, 15]
  });
};

function ChangeView({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, map.getZoom(), { animate: true });
    }
  }, [center, map]);
  return null;
}

export default function Rastreamento({ frotaList = [], userRole }) {
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(frotaList[0]?.placa || '');
  const [rotaIniciada, setRotaIniciada] = useState(false);
  const [statusGPS, setStatusGPS] = useState('Aguardando Permissão de GPS');
  const [velocidade, setVelocidade] = useState(0);
  const [coordenadas, setCoordenadas] = useState({ lat: -22.9068, lng: -43.1729 });
  const [todasPosicoes, setTodasPosicoes] = useState({});
  const [centroMapa, setCentroMapa] = useState([-22.9068, -43.1729]);

  // Mantém o veículo selecionado sincronizado com a frota carregada pela API.
  // Evita manter a placa fictícia PAC1000 quando frotaList chega após o primeiro render.
  useEffect(() => {
    if (!Array.isArray(frotaList) || frotaList.length === 0) return;

    const placaAtualExiste = frotaList.some(
      (veiculo) =>
        String(veiculo?.placa || '').trim().toUpperCase() ===
        String(veiculoSelecionado || '').trim().toUpperCase()
    );

    if (!placaAtualExiste) {
      setVeiculoSelecionado(frotaList[0].placa);
    }
  }, [frotaList, veiculoSelecionado]);

  const handleIniciarRota = () => {
    if (!navigator.geolocation) {
      alert('Seu navegador não suporta Geolocalização.');
      return;
    }

    setStatusGPS('Solicitando Permissão de GPS...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCoordenadas({ lat, lng });
        setCentroMapa([lat, lng]);
        setRotaIniciada(true);
        setStatusGPS('GPS Ativo & Transmitindo');

        api.post('/gps/atualizar', {
          placa: veiculoSelecionado,
          lat: lat,
          lng: lng,
          velocidade: 0,
          status: 'Em Movimento'
        }).catch(err => console.log("Erro ao iniciar GPS:", err));

        api.post('/operacao/iniciar-rota', {
          placa: veiculoSelecionado
        })
          .then((res) => {
            setStatusGPS(
              `GPS ativo • Entrega: ${res.data?.status_entrega || 'Em transporte'}`
            );
          })
          .catch((err) => {
            console.error('Erro ao atualizar status da entrega:', err.response || err);
            alert(
              err.response?.data?.detail ||
              'GPS iniciado, mas não foi possível mudar a entrega para Em transporte.'
            );
          });
      },
      (error) => {
        console.error(error);
        alert('Permissão de GPS negada ou indisponível.');
        setStatusGPS('Erro de Permissão GPS');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const simularCaminhaoAdmin = async () => {
    const placaTeste = frotaList[0]?.placa || '';
    if (!placaTeste) {
      alert('Nenhum veículo disponível para teste.');
      return;
    }
    try {
      await api.post('/gps/atualizar', {
        placa: placaTeste,
        lat: -22.9146,
        lng: -43.6542,
        velocidade: 45,
        status: 'Em Movimento'
      });
      setCentroMapa([-22.9146, -43.6542]);
      setTodasPosicoes(prev => ({
        ...prev,
        [placaTeste.replace(/-/g, "").toUpperCase()]: { placa: placaTeste, lat: -22.9146, lng: -43.6542, velocidade: 45, status: 'Em Movimento' }
      }));
    } catch (e) {
      console.error(e);
    }
  };

  // Transmissão contínua do motorista (Celular)
  useEffect(() => {
    let watchId;
    if (rotaIniciada && userRole === 'motorista') {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const velMs = position.coords.speed || 0;
          const velKmh = Math.round(velMs * 3.6);

          setCoordenadas({ lat, lng });
          setVelocidade(velKmh);
          setStatusGPS('Online (GPS Real Ativo)');

          api.post('/gps/atualizar', {
            placa: veiculoSelecionado,
            lat: lat,
            lng: lng,
            velocidade: velKmh,
            status: 'Em Movimento'
          }).catch(err => console.log("Erro ao enviar GPS:", err));
        },
        (error) => {
          console.log("Erro no rastreamento contínuo:", error);
          setStatusGPS('Sinal GPS instável');
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
      );
    }

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [rotaIniciada, userRole, veiculoSelecionado]);

  // Consulta contínua da gestão (Admin) - Atualiza e centraliza no caminhão real
  useEffect(() => {
    let gestorInterval;
    const buscarPosicoes = async () => {
      try {
        const res = await api.get('/gps/obter');
        const data = res.data;
        if (data && typeof data === 'object') {
          setTodasPosicoes(data);
          // Pega o primeiro veículo ativo e centraliza o mapa nele em tempo real
          const chaves = Object.keys(data);
          if (chaves.length > 0) {
            const veiculoAtivo = data[chaves[0]];
            if (veiculoAtivo && veiculoAtivo.lat && veiculoAtivo.lng) {
              setCentroMapa([veiculoAtivo.lat, veiculoAtivo.lng]);
            }
          }
        }
      } catch (err) {
        console.log("Erro ao buscar GPS do servidor:", err);
      }
    };

    if (userRole === 'admin') {
      buscarPosicoes();
      gestorInterval = setInterval(buscarPosicoes, 2000);
    }
    return () => clearInterval(gestorInterval);
  }, [userRole]);

  const latSegura = Number(coordenadas?.lat || -22.9068);
  const lngSegura = Number(coordenadas?.lng || -43.1729);

  const informarEntregaRealizada = async () => {
    if (!window.confirm(
      'Confirmar que a entrega foi realizada? A Central PS receberá o aviso para conferência.'
    )) return;

    try {
      const response = await api.post('/operacao/entrega-realizada', {
        placa: veiculoSelecionado
      });

      if (response.data?.aguardando_confirmacao) {
        alert(
          'Entrega informada à Central PS. O cliente continuará vendo "Em transporte" até a confirmação da Central.'
        );
      }
    } catch (err) {
      console.error('Erro ao informar entrega:', err.response || err);
      alert(
        err.response?.data?.detail ||
        'Não foi possível informar a entrega à Central.'
      );
    }
  };

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#fff' }}>
          {userRole === 'motorista' ? 'Transmissão de Rota (Motorista)' : 'Rastreamento GPS da Frota (Tempo Real)'}
        </h2>
        {userRole === 'admin' && (
          <button 
            onClick={simularCaminhaoAdmin}
            style={{ padding: '8px 14px', backgroundColor: '#32D74B', color: '#000', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
          >
            🧪 Centralizar no Veículo Real
          </button>
        )}
      </div>

      {userRole === 'motorista' && (
        <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <span style={{ fontSize: '12px', color: '#a0a0a0', fontWeight: '600', display: 'block', marginBottom: '6px' }}>SELECIONAR SEU VEÍCULO:</span>
            <select 
              value={veiculoSelecionado} 
              onChange={e => setVeiculoSelecionado(e.target.value)}
              disabled={rotaIniciada}
              style={{ width: '100%', padding: '12px', backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}
            >
              {frotaList.map(f => (
                <option key={f.id || f.placa} value={f.placa}>{f.placa} - {f.modelo}</option>
              ))}
            </select>
          </div>

          <div>
            {!rotaIniciada ? (
              <button 
                onClick={handleIniciarRota}
                style={{ width: '100%', padding: '14px', backgroundColor: '#32D74B', color: '#000', fontWeight: '800', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
              >
                ▶ INICIAR ROTA (HABILITAR GPS)
              </button>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                <button
                  onClick={informarEntregaRealizada}
                  style={{ width: '100%', padding: '14px', backgroundColor: '#FFD60A', color: '#000', fontWeight: '900', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
                >
                  ✅ ENTREGA REALIZADA
                </button>

                <button 
                  onClick={() => {
                    setRotaIniciada(false);
                    setStatusGPS('Rota Encerrada');
                    api.post('/gps/atualizar', {
                      placa: veiculoSelecionado,
                      lat: latSegura,
                      lng: lngSegura,
                      velocidade: 0,
                      status: 'Encerrado'
                    }).catch(() => {});
                  }}
                  style={{ width: '100%', padding: '14px', backgroundColor: '#FF3B30', color: '#fff', fontWeight: '800', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
                >
                  ⏹ ENCERRAR ROTA
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {userRole === 'motorista' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
          <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '12px', borderRadius: '8px' }}>
            <span style={{ fontSize: '11px', color: '#a0a0a0' }}>Status do Sinal</span>
            <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#32D74B', margin: '6px 0 0 0' }}>{statusGPS}</p>
          </div>
          <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '12px', borderRadius: '8px' }}>
            <span style={{ fontSize: '11px', color: '#a0a0a0' }}>Velocidade</span>
            <p style={{ fontSize: '22px', fontWeight: '800', color: '#007AFF', margin: '4px 0 0 0' }}>{velocidade} km/h</p>
          </div>
          <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '12px', borderRadius: '8px' }}>
            <span style={{ fontSize: '11px', color: '#a0a0a0' }}>Lat / Lng</span>
            <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#fff', margin: '8px 0 0 0', fontFamily: 'monospace' }}>{latSegura.toFixed(4)}, {lngSegura.toFixed(4)}</p>
          </div>
          <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '12px', borderRadius: '8px' }}>
            <span style={{ fontSize: '11px', color: '#a0a0a0' }}>Precisão</span>
            <p style={{ fontSize: '20px', fontWeight: '800', color: '#FFD60A', margin: '4px 0 0 0' }}>Alta (GPS Real)</p>
          </div>
        </div>
      )}

      {/* Visão do Gestor */}
      {userRole === 'admin' && (
        <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', borderRadius: '8px', overflow: 'hidden', height: '550px', width: '100%', position: 'relative' }}>
          <MapContainer 
            center={centroMapa} 
            zoom={15} 
            style={{ height: '100%', width: '100%', backgroundColor: '#111' }}
          >
            <ChangeView center={centroMapa} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {Object.values(todasPosicoes).map((veiculo, index) => {
              if (!veiculo || veiculo.status === 'Encerrado') return null;
              if (typeof veiculo.lat !== 'number' || typeof veiculo.lng !== 'number') return null;
              
              const dadosFrota = frotaList.find(f => f.placa.replace("-", "").toUpperCase() === veiculo.placa.replace("-", "").toUpperCase());
              const modeloVeiculo = dadosFrota ? dadosFrota.modelo : 'Caminhão';

              return (
                <Marker 
                  key={index} 
                  position={[veiculo.lat, veiculo.lng]}
                  icon={criarIconeCaminhao(veiculo.placa)}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                    <span>🚚 <b>{veiculo.placa}</b> ({modeloVeiculo})<br/>Velocidade: <b>{veiculo.velocidade} km/h</b><br/>Status: <b>{veiculo.status}</b></span>
                  </Tooltip>
                  <Popup>
                    Veículo: <b>{veiculo.placa}</b> ({modeloVeiculo})<br />
                    Velocidade: <b>{veiculo.velocidade} km/h</b><br />
                    Status: <b>{veiculo.status}</b>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      )}

      {userRole === 'motorista' && (
        <div style={{ backgroundColor: '#141414', border: '1px solid #2a2a2a', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
            Transmissão de rota ativa. Sua localização está sendo enviada em tempo real para o painel da gestão.
          </p>
        </div>
      )}
    </div>
  );
}